// One self-opening prompt at a time. Prompts that open on their own (review
// ask, dev-store upsell, founder call) claim this slot before showing, so they
// never stack on each other or on a modal or tour the user opened. Module-level
// on purpose: the brand layout remounts on every navigation.
import { useEffect } from "react";

let activePrompt: string | null = null;
const shownPrompts = new Set<string>();

/** True while any dialog, sheet or driver.js tour is on screen. */
export function isOverlayOpen(): boolean {
  if (document.body.classList.contains("driver-active")) return true;
  return document.querySelector('[role="dialog"], [role="alertdialog"]') !== null;
}

export function isAutoPromptActive(): boolean {
  return activePrompt !== null;
}

/** Fails while another prompt holds the slot or an overlay is open. */
export function claimAutoPrompt(id: string): boolean {
  if (activePrompt === id) return true;
  if (activePrompt !== null || isOverlayOpen()) return false;
  activePrompt = id;
  shownPrompts.add(id);
  return true;
}

export function releaseAutoPrompt(id: string): void {
  if (activePrompt === id) activePrompt = null;
}

/** Releases the slot when the prompt closes or its layout unmounts. */
export function useAutoPromptRelease(id: string, isOpen: boolean): void {
  useEffect(() => {
    if (!isOpen) return;
    return () => releaseAutoPrompt(id);
  }, [id, isOpen]);
}

/** Whether a different prompt has already opened since the page loaded. */
export function hasOtherAutoPromptShown(id: string): boolean {
  for (const shown of shownPrompts) {
    if (shown !== id) return true;
  }
  return false;
}
